import axios from 'axios';
import { getSupabaseAccessToken } from './supabase';
import { logger, reportClientError } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// In production (custom domain), use same-origin relative URL to avoid CORS issues.
// The custom domain proxies /api/* to the backend automatically.
const isCustomDomain = typeof window !== 'undefined' &&
  !window.location.hostname.includes('preview.emergentagent.com') &&
  !window.location.hostname.includes('localhost') &&
  !window.location.hostname.includes('emergent.host');
const API = isCustomDomain ? '/api' : `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Phase 10: attach Supabase Bearer access_token + CSRF double-submit token
// on every request. The backend's get_current_user prefers the Bearer
// (Supabase JWT) and only falls through to the legacy session_token cookie
// for email/password sign-ins.
api.interceptors.request.use(async (config) => {
  try {
    const token = await getSupabaseAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
      if (typeof window !== 'undefined' && window.__shipdesiAuthDebug) {
        // eslint-disable-next-line no-console
        console.info('[api] attached Supabase Bearer to', config.url, 'len=', token.length);
      }
    } else if (typeof window !== 'undefined' && window.__shipdesiAuthDebug) {
      // eslint-disable-next-line no-console
      console.info('[api] NO Supabase token at request time for', config.url);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[api] getSupabaseAccessToken threw:', e);
  }
  // CSRF double-submit: read csrf_token cookie (JS-readable) and mirror it into
  // X-CSRF-Token. Backend rejects mutating /api/* requests where the header
  // and cookie don't match.
  try {
    const m = typeof document !== 'undefined'
      ? document.cookie.match(/(?:^|; )csrf_token=([^;]+)/)
      : null;
    if (m) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = decodeURIComponent(m[1]);
    }
  } catch { /* ignore */ }
  return config;
});

// Response interceptor — show toast on API errors (silent catches get user feedback)
api.interceptors.response.use(
  response => response,
  error => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    const path = error.config?.url;
    
    // Log API error details to console
    logger.error(`[API Error] ${error.config?.method?.toUpperCase()} ${path} - Status: ${status || 'Network Error'}`, {
      detail,
      params: error.config?.params,
    });
    if (status >= 500) {
      reportClientError('API 5xx response', error, {
        method: error.config?.method?.toUpperCase(),
        path,
        status,
        correlationId: error.response?.headers?.['x-request-id'],
      });
    }

    // Don't toast for auth checks (401 on /auth/me is expected)
    const isAuthCheck = error.config?.url?.includes('/auth/me');
    const isEmailVerifyGate = error.response?.headers?.['x-error-code'] === 'EMAIL_NOT_VERIFIED';
    // Screens that show errors inline (3PL workspaces) pass { silent: true }.
    const isSilent = Boolean(error.config?.silent);
    if (!isAuthCheck && !isSilent && status && status >= 400 && !(status === 403 && isEmailVerifyGate)) {
      try {
        const { toast } = require('sonner');
        const msg = detail || (status === 403 ? 'Access denied' : status >= 500 ? 'Server error. Please try again.' : 'Request failed');
        toast.error(msg, { duration: 4000 });
      } catch {}
    }
    return Promise.reject(error);
  }
);

// Public
// Generic thin wrappers — return response.data directly.
export const apiGet = (path) => api.get(path).then(r => r.data);
export const apiPost = (path, data) => api.post(path, data).then(r => r.data);
export const apiPatch = (path, data) => api.patch(path, data).then(r => r.data);
export const apiDelete = (path) => api.delete(path).then(r => r.data);

/** Unwrap list from legacy bare array or paginated envelope ({ items, ... }). */
export function unwrapList(data, itemsKey = 'items') {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[itemsKey])) return data[itemsKey];
  return [];
}

export const getCountries = () => api.get('/countries');
export const getQuote = (country, weight) => api.post('/quote', { country, weight });

// Auth (Phase 11: Supabase Auth only — email/password + Google both via Supabase JWT)
export const syncSupabaseUser = () => api.post('/auth/sync-user');
export const getMe = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');
/** Sync Mongo mfa_enabled after Supabase enroll verify / unenroll (requires AAL2). */
export const patchMfaStatus = (mfaEnabled) => api.patch('/auth/mfa-status', { mfa_enabled: !!mfaEnabled });

// WebSocket one-time ticket (consumed on WS handshake, expires in 60s)
export const getWsTicket = () => api.post('/ws/ticket');

// User
export const getProfile = () => api.get('/users/profile');
export const updateKYC = (data) => api.put('/users/kyc', data); // legacy - use submitKYC for multipart

// Items
export const getItems = () => api.get('/items');
export const getMyItems = () => api.get('/items?scope=own');
export const createItem = (data) => api.post('/items', data);
export const updateItemStatus = (itemId, status) => api.patch(`/items/${itemId}/status`, { status });

// Packages
export const getPackages = (params = {}) => api.get('/packages', { params });
export const createPackage = (data) => api.post('/packages', data);
export const selectCourier = (packageId, courier) => api.patch(`/packages/${packageId}/courier`, { courier });

// Rates
export const compareRates = (weight, country, itemIds = [], itemCategories = []) => 
  api.post('/rates/compare', { weight, country, item_ids: itemIds, item_categories: itemCategories });

// Payments (Razorpay)
export const createPaymentOrder = (data) => api.post('/payments/create-order', data);
export const verifyPayment = (data) => api.post('/payments/verify', data);
export const refundPayment = (data) => api.post('/payments/refund', data);
export const getRazorpayKey = () => api.get('/payments/key');

// Surfaces a backend 403 EMAIL_NOT_VERIFIED response (consumed by AuthContext.checkAuth).
export const isEmailVerificationRequiredError = (error) => (
  error?.response?.status === 403
  && error?.response?.headers?.['x-error-code'] === 'EMAIL_NOT_VERIFIED'
);

// Tracking
export const getTracking = (trackingNumber) => api.get(`/tracking/${trackingNumber}`);

// Warehouse
export const warehouseScan = (formData) => api.post('/warehouse/scan', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const addItemPhotos = (itemId, formData) => api.post(`/warehouse/items/${itemId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getWarehouseItems = (params = {}) => api.get('/warehouse/items', { params });
export const getFileUrl = (path) => `${BACKEND_URL}/api/files/${path}`;
// Phase 11: protected files require a Supabase Bearer token. Use the axios
// instance (interceptor attaches the token) and return a blob object URL that
// callers can drop straight into an <img src> / anchor href. Remember to
// URL.revokeObjectURL() when done to avoid leaks.
export const fetchProtectedFileObjectUrl = async (path) => {
  const res = await api.get(`/files/${path}`, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
};
export const lookupLocker = (code) => api.get(`/warehouse/lookup-locker/${code}`);

// Saved Addresses
export const getAddresses = () => api.get('/addresses');
export const createAddress = (data) => api.post('/addresses', data);
export const updateAddress = (id, data) => api.put(`/addresses/${id}`, data);
export const deleteAddress = (id) => api.delete(`/addresses/${id}`);

// Admin
export const getAdminUsers = (params = {}) => api.get('/admin/users', { params });
export const updateUserRole = (userId, role) => api.patch(`/admin/users/${userId}/role`, { role });
export const deleteAdminUser = (userId) => api.delete(`/admin/users/${userId}`);
export const suspendAdminUser = (userId, suspended) => api.patch(`/admin/users/${userId}/suspend`, { suspended });
// Super-admin: manually confirm a user's email via Supabase Admin API.
// Pass { user_id } for a listed customer or { email } for someone not yet synced.
export const adminVerifyUserEmail = (payload) => apiPost('/admin/users/verify-email', payload);
export const bulkKycExport = (userIds) => api.post('/admin/users/bulk-kyc-export', { user_ids: userIds }, { responseType: 'arraybuffer' });
export const bulkKycArchive = (userIds) => api.post('/admin/users/bulk-kyc-archive', { user_ids: userIds });
export const bulkDeleteUsers = (ids) => api.post('/admin/users/bulk-delete', { ids });
export const getAdminStaff = () => api.get('/admin/staff');
export const getAdminRoles = () => api.get('/admin/roles');
export const getPermissionsMatrix = () => api.get('/admin/permissions-matrix');
export const updatePermissionsMatrix = (role, permissions) => api.put('/admin/permissions-matrix', { role, permissions });
export const bulkDeletePackages = (ids) => api.post('/admin/packages/bulk-delete', { ids });
export const deleteAdminPackage = (id) => api.delete(`/admin/packages/${id}`);
export const bulkDeleteDeclarations = (ids) => api.post('/admin/declarations/bulk-delete', { ids });
export const deleteAdminDeclaration = (id) => api.delete(`/admin/declarations/${id}`);
export const bulkDeleteItems = (ids) => api.post('/admin/items/bulk-delete', { ids });
export const deleteAdminItem = (id) => api.delete(`/admin/items/${id}`);
export const bulkDeleteBlogPosts = (ids) => api.post('/admin/blog/posts/bulk-delete', { ids });
export const getAdminShipments = (params = {}) => api.get('/admin/shipments', { params });
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminDashboardMetrics = (params = {}) => api.get('/admin/dashboard/metrics', { params });
export const getAdminUserActivity = (userId) => api.get(`/admin/users/${userId}/activity-log`);
export const getAdminIncomingDeclarations = (params = {}) => api.get('/admin/incoming-declarations', { params });
export const approveKYC = (userId, status) => api.patch(`/admin/users/${userId}/kyc`, { kyc_status: status });

// Settings
export const getSettings = () => api.get('/admin/settings');
export const updateSettings = (data) => api.put('/admin/settings', data);

// Admin Orders
export const getAdminOrders = (params = {}) => api.get('/admin/orders', { params });
export const updateOrderStatus = (packageId, orderStatus, awbNumber) => api.patch(`/admin/orders/${packageId}/status`, { order_status: orderStatus, awb_number: awbNumber });
export const validateAwb = (awbNumber, courier) => api.post('/admin/validate-awb', { awb_number: awbNumber, courier });

// Margin Config
export const getMarginConfig = () => api.get('/admin/margin-config');
export const updateMarginConfig = (data) => api.put('/admin/margin-config', data);

// Cancel Shipment
export const cancelShipment = (packageId) => api.post(`/shipments/${packageId}/cancel`);

// Admin Pending Tasks
export const getAdminPendingTasks = () => api.get('/admin/pending-tasks');

// Admin Transactions (payments / Razorpay)
export const getAdminTransactions = (params) =>
  api.get('/admin/transactions', { params });

export const getAdminTransaction = (paymentId) =>
  api.get(`/admin/transactions/${encodeURIComponent(paymentId)}`);

export const getAdminTransactionTimeline = (paymentId) =>
  api.get(`/admin/transactions/${encodeURIComponent(paymentId)}/timeline`);

export const syncAdminTransaction = (paymentId) =>
  api.post(`/admin/transactions/${encodeURIComponent(paymentId)}/sync`);

export const refundAdminTransaction = (paymentId, body) =>
  api.post(`/admin/transactions/${encodeURIComponent(paymentId)}/refund`, body);

/** CSV download — caller triggers browser save from response.data Blob */
export const exportAdminTransactionsCsv = (params) =>
  api.get('/admin/transactions/export', { params, responseType: 'blob' });

// Chat
export const createConversation = (data) => api.post('/chat/conversations', data);
export const getConversations = (params = {}) => api.get('/chat/conversations', { params });
export const getMessages = (conversationId) => api.get(`/chat/conversations/${conversationId}/messages`);
export const getUnreadCount = () => api.get('/chat/unread');
export const closeConversation = (conversationId) => api.patch(`/chat/conversations/${conversationId}/close`);
export const reopenConversation = (conversationId) => api.patch(`/chat/conversations/${conversationId}/reopen`);
export const initiateChatByLocker = (data) => api.post('/chat/initiate-by-locker', data);

// Carriers
export const getCarriers = () => api.get('/carriers');
export const getItemCategories = () => api.get('/item-categories');
export const getAdminCarriers = () => api.get('/admin/carriers');
export const updateCarrier = (carrierId, data) => api.put(`/admin/carriers/${carrierId}`, data);

// Aggregators & Services
export const getAdminAggregators = () => api.get('/admin/aggregators');
export const createAggregator = (data) => api.post('/admin/aggregators', data);
export const updateAggregator = (aggId, data) => api.put(`/admin/aggregators/${aggId}`, data);
export const deleteAggregator = (aggId) => api.delete(`/admin/aggregators/${aggId}`);
export const getAdminServices = () => api.get('/admin/services');
export const createService = (data) => api.post('/admin/services', data);
export const updateService = (svcId, data) => api.put(`/admin/services/${svcId}`, data);
export const deleteService = (svcId) => api.delete(`/admin/services/${svcId}`);

// Live Services (Garudavega)
export const getAdminLiveServices = () => api.get('/admin/live-services');
export const updateServiceOverrides = (overrides) => api.put('/admin/live-services/overrides', { overrides });

// Manual Carriers
export const getManualCarriers = () => api.get('/admin/manual-carriers');
export const createManualCarrier = (data) => api.post('/admin/manual-carriers', data);
export const updateManualCarrier = (id, data) => api.put(`/admin/manual-carriers/${id}`, data);
export const deleteManualCarrier = (id) => api.delete(`/admin/manual-carriers/${id}`);

// Static Pages (public legal/policy docs editable from admin)
export const getPublicStaticPages = () => api.get('/public/static-pages');
export const getPublicStaticPage = (slug) => api.get(`/public/static-pages/${slug}`);
export const getAdminStaticPages = () => api.get('/admin/static-pages');
export const updateAdminStaticPage = (slug, data) => api.put(`/admin/static-pages/${slug}`, data);

// Admin Order inline edit (carrier / tracking / etc — audited)
export const adminEditOrder = (packageId, data) => api.patch(`/admin/orders/${packageId}/edit`, data);

// Signup Coupons
export const validateCoupon = (code) => api.post('/coupons/validate', { code });
export const redeemCoupon = (code) => api.post('/coupons/redeem', { code });
export const getAdminCoupons = () => api.get('/admin/coupons');
export const createCoupon = (data) => api.post('/admin/coupons', data);
export const updateCoupon = (code, data) => api.patch(`/admin/coupons/${code}`, data);
export const deleteCoupon = (code) => api.delete(`/admin/coupons/${code}`);
export const getCouponRedemptions = (code) => api.get(`/admin/coupons/${code}/redemptions`);
export const getCouponAnalytics = (code) => api.get(`/admin/coupons/${code}/analytics`);

// Document Requirements
export const getDocumentRequirements = () => api.get('/admin/document-requirements');
export const createDocumentRequirement = (data) => api.post('/admin/document-requirements', data);
export const updateDocumentRequirement = (id, data) => api.put(`/admin/document-requirements/${id}`, data);
export const deleteDocumentRequirement = (id) => api.delete(`/admin/document-requirements/${id}`);
export const checkRequiredDocuments = (data) => api.post('/shipments/check-documents', data);

// Item Categories (dynamic) - getItemCategories already defined above
export const updateItemCategories = (categories) => api.put('/admin/item-categories', { categories });

// Weight Config
export const getWeightConfig = () => api.get('/weight-config');
export const updateWeightConfig = (data) => api.put('/admin/weight-config', data);

// Blog
export const getPublicBlogPosts = (limit = 20, skip = 0) => api.get(`/blog/posts?limit=${limit}&skip=${skip}`);
export const getBlogPostBySlug = (slug) => api.get(`/blog/posts/${slug}`);
export const getAdminBlogPosts = () => api.get('/admin/blog/posts');
export const createBlogPost = (data) => api.post('/admin/blog/posts', data);
export const updateBlogPost = (id, data) => api.put(`/admin/blog/posts/${id}`, data);
export const deleteBlogPost = (id) => api.delete(`/admin/blog/posts/${id}`);
export const uploadPackageDocument = (packageId, docId, file) => {
  const fd = new FormData(); fd.append('file', file); fd.append('doc_id', docId);
  return api.post(`/packages/${packageId}/documents`, fd);
};
export const getPackageDocuments = (packageId) => api.get(`/packages/${packageId}/documents`);
export const submitPackageDocuments = (packageId) => api.post(`/packages/${packageId}/submit-documents`);
export const adminReviewDocuments = (packageId, data) => api.post(`/admin/packages/${packageId}/review-documents`, data);

// Admin Testing / Sandbox
export const adminCreateTestItem = (data) => api.post('/admin/test/create-item', data);
export const adminCreateTestDeclaration = (data) => api.post('/admin/test/create-declaration', data);
export const adminCleanupTestData = (params = {}) => api.delete('/admin/test/cleanup', { params });

// Logo Config
export const getLogoConfig = () => api.get('/logo-config');
export const updateLogoConfig = (data) => api.put('/admin/logo-config', data);
export const uploadLogo = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/admin/logo-upload', fd); };
export const uploadCarrierLogo = (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/admin/carrier-logo-upload', fd); };

// Subscription Plans
export const getPlans = () => api.get('/plans');
export const getMySubscription = () => api.get('/subscription');
export const subscribeToPlan = (planId) => api.post('/subscription/subscribe', { plan_id: planId });
export const verifySubscription = (data) => api.post('/subscription/verify', data);
export const cancelSubscription = () => api.post('/subscription/cancel');
export const calculatePricing = (data) => api.post('/pricing/calculate', data);
export const getAdminPlans = () => api.get('/admin/plans');
export const updateAdminPlan = (planId, data) => api.put(`/admin/plans/${planId}`, data);
export const seedPlans = () => api.post('/admin/plans/seed');
export const getAdminSubscriptions = () => api.get('/admin/subscriptions');

// KYC
export const getIdTypes = (country) => api.get(`/kyc/id-types/${country}`);
export const submitKYC = (formData) => api.put('/users/kyc', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getProhibitedItems = () => api.get('/prohibited-items');
export const getKYCDocument = (userId) => `${API}/admin/users/${userId}/kyc-document`;

// Declarations (Incoming Pre-Alert)
export const getInwardCouriers = () => api.get('/inward-couriers');
export const createDeclaration = (data) => api.post('/declarations', data);
export const getDeclarations = (params = {}) => api.get('/declarations', { params });
export const getDeclarationByAwb = (awb) => api.get(`/declarations/by-awb/${awb}`);
export const updateDeclarationStatus = (id, data) => api.patch(`/declarations/${id}/status`, data);
export const getAdminDeclarations = (params = {}) => api.get('/admin/declarations', { params });

// Wallet
export const getWallet = () => api.get('/wallet');
export const walletTopup = (amount) => api.post('/wallet/topup', { amount });
export const walletTopupVerify = (data) => api.post('/wallet/topup/verify', data);

// Personal Shopper
export const createShopperRequest = (data) => api.post('/shopper/requests', data);
export const getShopperRequests = () => api.get('/shopper/requests');
export const getAdminShopperRequests = (params = {}) => api.get('/admin/shopper/requests', { params });
export const updateShopperRequest = (requestId, data) => api.patch(`/admin/shopper/requests/${requestId}`, data);
export const getAdminUserPlan = (userId) => api.get(`/admin/users/${userId}/plan`);
// Sprint 2 — Personal Shopper credibility
export const uploadShopperProof = (requestId, formData) => api.post(`/admin/shopper/requests/${requestId}/proof`, formData);
export const deleteShopperProof = (requestId, docId) => api.delete(`/admin/shopper/requests/${requestId}/proof/${docId}`);
export const proposeShopperSubstitution = (requestId, data) => api.post(`/admin/shopper/requests/${requestId}/propose-substitution`, data);
export const decideShopperSubstitution = (requestId, decision) => api.post(`/shopper/requests/${requestId}/substitution-decision`, { decision });
export const getShopperExceptionReasons = () => api.get('/admin/shopper/exception-reasons');
export const flagShopperException = (requestId, data) => api.post(`/admin/shopper/requests/${requestId}/flag-exception`, data);
// Sprint 3 — Forwarding exceptions, insurance, open-check
export const getPackageExceptionReasons = () => api.get('/admin/packages/exception-reasons');
export const flagPackageException = (packageId, data) => api.post(`/admin/packages/${packageId}/flag-exception`, data);
export const clearPackageException = (packageId, data) => api.post(`/admin/packages/${packageId}/clear-exception`, data);
export const getInsuranceQuote = (declaredValue) => api.get(`/insurance/quote?declared_value_inr=${declaredValue}`);
export const fileInsuranceClaim = (packageId, data) => api.post(`/packages/${packageId}/insurance-claim`, data);
export const getMyInsuranceClaims = () => api.get('/insurance-claims');
export const adminGetInsuranceClaims = () => api.get('/admin/insurance-claims');
export const adminUpdateInsuranceClaim = (claimId, data) => api.patch(`/admin/insurance-claims/${claimId}`, data);
export const requestOpenCheck = (itemId) => api.post(`/items/${itemId}/request-open-check`);
export const adminCompleteOpenCheck = (itemId, data) => api.post(`/admin/items/${itemId}/complete-open-check`, data);
export const adminGetOpenCheckQueue = () => api.get('/admin/open-check-queue');
// Contact form
export const submitContactMessage = (data) => api.post('/contact', data);
export const adminListContactMessages = () => api.get('/admin/contact-messages');
export const adminUpdateContactMessage = (messageId, data) => api.patch(`/admin/contact-messages/${messageId}`, data);
// Email configuration
export const adminGetEmailConfig = () => api.get('/admin/email-config');
export const adminSaveEmailConfig = (data) => api.put('/admin/email-config', data);
export const adminTestEmail = (data) => api.post('/admin/email-config/test', data);
export const acceptShopperQuote = (requestId, data) => api.post(`/shopper/requests/${requestId}/accept`, data);
export const cancelShopperRequest = (requestId) => api.post(`/shopper/requests/${requestId}/cancel`);
export const shopperPaymentStatus = (requestId) => api.get(`/shopper/requests/${requestId}/payment-status`);

// Returns
export const checkReturnEligibility = (itemId) => api.get(`/returns/eligibility/${itemId}`);
export const createReturnRequest = (formData) => api.post('/returns', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getMyReturns = () => api.get('/returns');
export const cancelMyReturn = (returnId) => api.post(`/returns/${returnId}/cancel`);
export const getAdminReturns = (params = {}) => api.get('/admin/returns', { params });
export const updateAdminReturn = (returnId, data) => api.patch(`/admin/returns/${returnId}`, data);
export const refundReturn = (returnId, amount) => api.post(`/admin/returns/${returnId}/refund`, { amount });

// Warehouse bulk receive (Fulfillment SKU split)
export const warehouseBulkReceive = (formData) => api.post('/warehouse/bulk-receive', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

// HSN code suggestions (declare-incoming flow)
export const hsnSuggest = (q, category, limit = 10) => api.get(`/hsn/suggest`, { params: { q: q || '', category: category || '', limit } });

// Customer order-proof image upload (for declarations)
export const uploadDeclarationProof = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/declarations/upload-proof', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Postal code → city/state lookup (Zippopotam)
export const lookupPostcode = (country, postcode) => api.get('/address/lookup', { params: { country, postcode } });

// Public branding config (favicon, logo, name) — no auth
export const getPublicConfig = () => api.get('/public/branding');

// Admin favicon upload
export const uploadFavicon = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/admin/favicon-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

// Public content (no auth)
export const getPublicContent = () => api.get('/public/content');
export const getPublicShippingPreferences = () => api.get('/public/shipping-preferences');
export const getPublicMarketingBanner = () => api.get('/public/marketing-banner');

// User Shipping Preferences
export const getUserShippingPrefs = () => api.get('/user/shipping-preferences');
export const updateUserShippingPrefs = (data) => api.put('/user/shipping-preferences', data);

// Admin Platform Config (UI, Content, SEO, Marketing)
export const getPlatformConfig = (key) => api.get(`/admin/platform/${key}`);
export const updatePlatformConfig = (key, data) => api.put(`/admin/platform/${key}`, data);

// SEO Management
export const getSeoGlobal = () => api.get('/seo/global');
export const updateSeoGlobal = (data) => api.put('/seo/global', data);
export const getSeoTracking = () => api.get('/seo/tracking');
export const updateSeoTracking = (data) => api.put('/seo/tracking', data);
export const getSeoRobotsConfig = () => api.get('/seo/robots-config');
export const updateSeoRobotsConfig = (data) => api.put('/seo/robots-config', data);
export const getSeoPages = () => api.get('/seo/pages');export const updateSeoPage = (slug, data) => api.put(`/seo/pages/${slug}`, data);
export const uploadSeoImage = (formData) => api.post('/seo/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getSeoRender = (path) => api.get(`/seo/render?path=${encodeURIComponent(path)}`);

// Admin Backup & Restore
export const getBackupInfo = () => api.get('/admin/backup/info');
export const exportBackup = () => api.get('/admin/backup/export', { responseType: 'blob' });
export const restoreBackup = (data) => api.post('/admin/backup/restore', data);
export const getRestoreHistory = () => api.get('/admin/backup/restore-history');

// Admin Database Stats
export const getDatabaseStats = () => api.get('/admin/database/stats');

// Customer confirm delivery
export const confirmDelivery = (packageId) => api.post(`/packages/${packageId}/confirm-delivery`);

// Phase 14d / C1 — per-page content overrides (Visual Content Editor)
export const getPublicPageContent = (slug) => api.get(`/public/page-content/${slug}`);
export const getAdminPageContent = (slug) => api.get(`/admin/page-content/${slug}`);
export const updateAdminPageContent = (slug, sections) => api.put(`/admin/page-content/${slug}`, { sections });

// Garudavega Remote Area Surcharge probe (per-pincode)
export const checkRemoteAreaSurcharge = (payload) => api.post('/courier/remote-area-check', payload);

// Undeclared packages (warehouse + super admin)
export const registerUndeclared = (formData) => api.post('/warehouse/undeclared', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const listUndeclared = (status, extraParams = {}) => api.get('/admin/undeclared', { params: { ...(status ? { status } : {}), ...extraParams } });
export const moveUndeclaredToLocker = (id, payload) => api.post(`/admin/undeclared/${id}/move-to-locker`, payload);
export const discardUndeclared = (id, reason) => api.post(`/admin/undeclared/${id}/discard`, { reason });

// Display currencies (amounts are stored/charged in INR; these only drive display)
export const getFxRates = () => api.get('/fx/rates');
export const getMyCurrency = () => api.get('/me/currency');
export const setMyCurrency = (currency) => api.put('/me/currency', { currency: currency || null });
export const getAdminFxRates = () => api.get('/admin/fx-rates');
export const saveAdminFxOverrides = (overrides, marginPct) => api.put('/admin/fx-rates/overrides', {
  overrides, ...(marginPct === undefined ? {} : { margin_pct: marginPct }),
});
export const refreshAdminFxRates = () => api.post('/admin/fx-rates/refresh');
export const getAdminUserCurrency = (userId) => api.get(`/admin/users/${userId}/display-currency`);
export const setAdminUserCurrency = (userId, currency) => api.put(`/admin/users/${userId}/display-currency`, { currency: currency || null });

export default api;
